import { useParams, Link } from 'react-router-dom'
import { isValidUrl } from '../utils/validation'
import './TutorialDetail.css'

interface Resource {
  id: string
  type: 'video' | 'code' | 'document' | 'link'
  title: string
  url: string
}

interface Tutorial {
  id: string
  title: string
  description: string
  category: string
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  duration: string
  videoUrl?: string
  codeUrl?: string
  resources?: Resource[]
  createdAt: string
  content?: string
}

const TutorialDetail = () => {
  const { id } = useParams<{ id: string }>()

  // Load tutorial from localStorage
  let tutorials: Tutorial[] = []
  try {
    tutorials = JSON.parse(localStorage.getItem('tutorials') || '[]')
  } catch (e) {
    console.error('Error loading tutorials:', e)
  }
  const tutorial = tutorials.find((t: Tutorial) => t.id === id) as Tutorial | undefined

  if (!tutorial) {
    return (
      <main className="main-content tutorial-detail-page">
        <div className="tutorial-detail-container">
          <h1>tutorial not found</h1>
          <Link to="/community?tab=tutorials" className="tutorial-back-link">
            back to tutorials
          </Link>
        </div>
      </main>
    )
  }

  return (
    <main className="main-content tutorial-detail-page">
      <div className="tutorial-detail-container">
        <Link to="/community?tab=tutorials" className="tutorial-back-link">
          <i className="fas fa-arrow-left"></i>
          back to tutorials
        </Link>

        <div className="tutorial-detail-header">
          <div className="tutorial-detail-meta">
            <span className="tutorial-detail-category">{tutorial.category}</span>
            <span className={`tutorial-detail-difficulty ${tutorial.difficulty}`}>
              {tutorial.difficulty}
            </span>
            <span className="tutorial-detail-duration">
              <i className="fas fa-clock"></i>
              {tutorial.duration}
            </span>
          </div>
          <h1 className="tutorial-detail-title">{tutorial.title}</h1>
          <p className="tutorial-detail-description">{tutorial.description}</p>
        </div>

        <div className="tutorial-detail-content">
          <div className="tutorial-detail-main">
            <div className="tutorial-detail-section">
              <h2>overview</h2>
              <p>
                {tutorial.content || `This ${tutorial.difficulty} tutorial will guide you through ${tutorial.title.toLowerCase()}. 
                You'll learn the fundamentals and build practical skills that you can apply to your own projects. 
                This tutorial is designed for ${tutorial.difficulty} level users and should take approximately ${tutorial.duration} to complete.`}
              </p>
            </div>

            <div className="tutorial-detail-section">
              <h2>what you'll learn</h2>
              <ul className="tutorial-detail-list">
                <li>Core concepts and fundamentals</li>
                <li>Practical implementation techniques</li>
                <li>Best practices and optimization</li>
                <li>Real-world application examples</li>
              </ul>
            </div>

            <div className="tutorial-detail-section">
              <h2>prerequisites</h2>
              <ul className="tutorial-detail-list">
                <li>Basic understanding of the topic</li>
                <li>Access to required tools and software</li>
                <li>Willingness to experiment and learn</li>
              </ul>
            </div>
          </div>

          <div className="tutorial-detail-sidebar">
            {tutorial.resources && tutorial.resources.length > 0 && (
              <div className="tutorial-detail-actions">
                <h3 style={{ fontSize: '16px', marginBottom: '12px', textTransform: 'lowercase', letterSpacing: '1px' }}>resources</h3>
                <div className="tutorial-detail-resources">
                  {tutorial.resources.filter((resource: any) => isValidUrl(resource.url)).map((resource: any) => (
                    <a
                      key={resource.id}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tutorial-detail-resource-link"
                    >
                      <i className={`fas ${
                        resource.type === 'video' ? 'fa-video' :
                        resource.type === 'code' ? 'fa-code' :
                        resource.type === 'document' ? 'fa-file-alt' :
                        'fa-link'
                      }`}></i>
                      {resource.title}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="tutorial-detail-info-card">
              <h3>tutorial info</h3>
              <div className="tutorial-detail-info-item">
                <span className="tutorial-detail-info-label">category</span>
                <span className="tutorial-detail-info-value">{tutorial.category}</span>
              </div>
              <div className="tutorial-detail-info-item">
                <span className="tutorial-detail-info-label">difficulty</span>
                <span className="tutorial-detail-info-value">{tutorial.difficulty}</span>
              </div>
              <div className="tutorial-detail-info-item">
                <span className="tutorial-detail-info-label">duration</span>
                <span className="tutorial-detail-info-value">{tutorial.duration}</span>
              </div>
              <div className="tutorial-detail-info-item">
                <span className="tutorial-detail-info-label">created</span>
                <span className="tutorial-detail-info-value">
                  {new Date(tutorial.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}

export default TutorialDetail

